import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable, shareReplay, Subject, tap } from 'rxjs';
import { OrganizationSetupFormService } from '../../contract-management/services/organization-setup-from.service';
import { PaymentService } from '../../contract-management/services/payment.service';
import { SUBSCRIPTION_STATUS, TRIAL_LIFECYCLE_PHASE } from '../models/enums';
import { ISubscriptionDetails, ISubscriptionResponse, PAYMENT_STATUS } from '../models/model';
import { calculateTrialDaysFromDuration, calculateTrialDaysFromEndDate } from '../utils/trial-calculation.utils';
import { LLM_PROVIDERS } from 'src/app/features/general-agent/models/model';
import { IPlan } from 'src/app/pricing/models';

/**
 * Centralized subscription state service using Angular signals.
 * Provides reactive access to subscription details across the application.
 * 
 * IMPORTANT: This service maintains a single source of truth for subscription data.
 * 
 * ## Usage Pattern:
 * 
 * 1. **AUTOMATIC FETCH ON SIGN-IN** (Handled in AppComponent):
 * ```typescript
 * // Automatically fetched in AppComponent when user signs in
 * // Always fetches fresh data on sign-in using refreshSubscriptionDetails()
 * ```
 * 
 * 2. **READ CACHED DATA** (All other components):
 * ```typescript
 * // In any other component:
 * const details = this.subscriptionState.subscriptionDetails(); // ✅ Uses cache
 * const plan = this.subscriptionState.plan(); // ✅ Uses cache
 * const isActive = this.subscriptionState.isActive(); // ✅ Uses cache
 * 
 * // ❌ DO NOT call fetchSubscriptionDetails() or refreshSubscriptionDetails() in other components!
 * ```
 * 
 * 3. **REFRESH ONLY WHEN NEEDED** (After payment/subscription changes):
 * ```typescript
 * // Only call when subscription actually changes (e.g., after payment):
 * this.subscriptionState.refreshSubscriptionDetails().subscribe();
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class SubscriptionStateService {
  private organizationService = inject(OrganizationSetupFormService);
  private paymentService = inject(PaymentService);

  // Writable signal for subscription response
  private _subscriptionDetails = signal<ISubscriptionResponse | null>(null);

  // Track which user ID the subscription details belong to
  private _subscriptionUserId: string | null = null;

  // Writable signal to control dialog visibility (can be manually dismissed)
  private _isDialogDismissed = signal<boolean>(false);

  // Subject to imperatively trigger the subscription ended dialog from anywhere
  public readonly showSubscriptionDialog$ = new Subject<void>();

  // Writable signal to control sidebar lock state (when user clicks "Activate your plan")
  private _isSidebarLocked = signal<boolean>(false);

  // Public readonly signals
  public readonly subscriptionDetails = this._subscriptionDetails.asReadonly();

  // Computed signals for easy access to nested properties
  public readonly plan = computed(() => this._subscriptionDetails()?.plan || null);
  public readonly subscriptions = computed(() => this._subscriptionDetails()?.subscriptions || null);
  public readonly category = computed(() => this._subscriptionDetails()?.category || null);

  /**
   * AI providers enabled on this subscription (plan-level setting).
   * Falls back to all providers when the field is absent (e.g. existing subscriptions).
   */
  public readonly allowedModels = computed<LLM_PROVIDERS[]>(() => {
    const modelAccess = this.subscriptions()?.modelAccess;
    if (modelAccess && modelAccess.length > 0) {
      return modelAccess;
    }
    return Object.values(LLM_PROVIDERS);
  });

  // Computed subscription status
  public readonly subscriptionStatus = computed(() =>
    this._subscriptionDetails()?.subscriptions?.status || null
  );

  // Computed boolean flags
  public readonly isActive = computed(() =>
    this.subscriptionStatus() === SUBSCRIPTION_STATUS.ACTIVE
  );

  /**
   * Check if user is in active trial period
   * Trial is active when current date is between trialStartDate and trialEndDate
   */
  public readonly isTrial = computed(() => {
    const subscription = this.subscriptions();
    if (!subscription || !subscription.trialStartDate || !subscription.trialEndDate) return false;
    if (subscription.trialExpiryProcessed) return false;
    if (subscription.paymentStatus === PAYMENT_STATUS.PAID) return false;

    const trialStartDate = new Date(subscription.trialStartDate);
    const trialEndDate = new Date(subscription.trialEndDate);
    const currentDate = new Date();

    return currentDate >= trialStartDate && currentDate <= trialEndDate;
  });

  public readonly isInactive = computed(() =>
    this.subscriptionStatus() === SUBSCRIPTION_STATUS.INACTIVE
  );

  // Computed trial days left as number (for logic/comparisons)
  public readonly trialDaysLeft = computed(() => {
    const subscription = this.subscriptions();
    if (!subscription || !subscription.trialStartDate) {
      return null;
    }

    // Prefer end date if available, otherwise use duration with term
    if (subscription.trialEndDate) {
      return calculateTrialDaysFromEndDate(
        subscription.trialEndDate,
        true
      ) as number;
    } else if (subscription.trial?.duration) {
      return calculateTrialDaysFromDuration(
        subscription.trialStartDate,
        subscription.trial.duration,
        subscription.trial.term,
        true
      ) as number;
    }

    return 0;
  });

  // Computed trial days left as formatted string (for display)
  public readonly trialDaysLeftFormatted = computed(() => {
    const remainingDays = this.trialDaysLeft();

    if (remainingDays === null || remainingDays === undefined) {
      return '0 days';
    }

    // Format based on remaining time
    if (remainingDays <= 0) {
      return '0 days';
    } else if (remainingDays >= 7) {
      const weeks = Math.floor(remainingDays / 7);
      const remainingDaysAfterWeeks = remainingDays % 7;

      if (remainingDaysAfterWeeks === 0) {
        return `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
      } else {
        return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ${remainingDaysAfterWeeks} ${remainingDaysAfterWeeks === 1 ? 'day' : 'days'}`;
      }
    } else {
      return `${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`;
    }
  });

  /**
   * Check if user has paid (paymentStatus is PAID)
   * This is the PRIMARY indicator of payment status
   */
  public readonly hasPaid = computed(() => {
    const subscription = this.subscriptions();
    return subscription?.paymentStatus === PAYMENT_STATUS.PAID;
  });

  /** True when the org has an active Stripe subscription (paid + active + has subscriptionId). */
  public readonly hasActiveStripeSubscription = computed(() => {
    const subscription = this.subscriptions();
    return !!(
      subscription?.subscriptionId &&
      subscription.paymentStatus === PAYMENT_STATUS.PAID &&
      subscription.status === SUBSCRIPTION_STATUS.ACTIVE
    );
  });

  /** True when the subscription is scheduled to cancel at the end of the billing cycle. */
  public readonly isCancelPending = computed(() => {
    return this.subscriptions()?.cancelPendingAtPeriodEnd === true;
  });

  /** True when the last payment attempt failed (Stripe invoice past due, card declined, etc.). */
  public readonly isPaymentFailed = computed(() => {
    return this.subscriptions()?.paymentStatus === PAYMENT_STATUS.FAILED;
  });

  /**
   * True when payment has not been made and the org is not in an active trial.
   * Covers UNPAID (trial ended without payment) and UNSUBSCRIBED states.
   */
  public readonly isPaymentUnpaid = computed(() => {
    const status = this.subscriptions()?.paymentStatus;
    return (
      status === PAYMENT_STATUS.UNPAID ||
      status === PAYMENT_STATUS.UNSUBSCRIBED
    ) && !this.isTrial();
  });


  /** True when a downgrade is scheduled for the next billing date. */
  public readonly isDowngradePending = computed(() =>
    !!this.subscriptions()?.pendingDowngrade
  );

  /** The pending downgrade details, or null if none. */
  public readonly pendingDowngradeInfo = computed(() =>
    this.subscriptions()?.pendingDowngrade ?? null
  );


  /**
   * Check if trial has expired without payment
   * This means: trialEndDate exists and passed, but payment status is not PAID
   */
  public readonly isTrialExpired = computed(() => {
    const subscription = this.subscriptions();
    if (!subscription || !subscription.trialEndDate || subscription.paymentStatus === PAYMENT_STATUS.PAID) {
      return false;
    }

    const trialEndDate = new Date(subscription.trialEndDate);
    const currentDate = new Date();

    return trialEndDate < currentDate;
  });

  public readonly trialLifecyclePhase = computed(() =>
    this.subscriptions()?.trialLifecyclePhase ?? null
  );

  /**
   * Has subscription actually ended (for route guards - ignores dialog dismissal)
   * 
   * Logic: First check if in trial, if not in trial, check payment status
   * 
   * Subscription has ended when:
   * - Status is INACTIVE, OR
   * - Not in trial AND payment status is not PAID
   * 
   * Subscription has NOT ended when:
   * - Status is ACTIVE and in trial, OR
   * - Status is ACTIVE and paymentStatus is PAID
   */
  public readonly hasSubscriptionEnded = computed(() => {
    const subscription = this.subscriptions();

    if (!subscription) {
      return true;
    }

    // If status is INACTIVE, subscription has ended
    if (subscription.status === SUBSCRIPTION_STATUS.INACTIVE) {
      return true;
    }

    if (subscription.status === SUBSCRIPTION_STATUS.ACTIVE) {
      // First check: If in trial, subscription has NOT ended
      if (this.isTrial()) {
        return false;
      }

      // Second check: If not in trial, check payment status
      // If payment status is PAID, subscription has NOT ended
      if (subscription.paymentStatus === PAYMENT_STATUS.PAID) {
        return false;
      }

      // Not in trial and payment not made - subscription has ended
      return true;
    }

    return false;
  });

  // Computed: Is subscription ended (for dialog display - respects dialog dismissal)
  public readonly isSubscriptionEnded = computed(() => {
    // Use the actual subscription ended status
    const hasEnded = this.hasSubscriptionEnded();

    // If subscription hasn't ended, dialog shouldn't show
    if (!hasEnded) {
      return false;
    }

    return true;
  });

  /** Emits whenever the subscription is refreshed due to a real-time socket update. */
  public readonly socketRefresh$ = new Subject<void>();

  // --- Org-level subscription (from PaymentService) ---

  private _orgSubscriptionDetails = signal<(ISubscriptionDetails & { plan?: IPlan }) | null>(null);
  public readonly orgSubscriptionDetails = this._orgSubscriptionDetails.asReadonly();

  public readonly orgPlan = computed(() => this._orgSubscriptionDetails()?.plan ?? null);

  public readonly orgHasPaid = computed(() =>
    this._orgSubscriptionDetails()?.paymentStatus === PAYMENT_STATUS.PAID
  );

  public readonly orgIsActive = computed(() =>
    this._orgSubscriptionDetails()?.status === SUBSCRIPTION_STATUS.ACTIVE
  );

  public readonly orgIsPaymentFailed = computed(() =>
    this._orgSubscriptionDetails()?.paymentStatus === PAYMENT_STATUS.FAILED
  );

  public readonly orgIsTrial = computed(() => {
    const sub = this._orgSubscriptionDetails();
    if (!sub || !sub.trialStartDate || !sub.trialEndDate) return false;
    if (sub.trialExpiryProcessed) return false;
    if (sub.paymentStatus === PAYMENT_STATUS.PAID) return false;
    const now = new Date();
    return now >= new Date(sub.trialStartDate) && now <= new Date(sub.trialEndDate);
  });

  public readonly orgIsPaymentUnpaid = computed(() => {
    const s = this._orgSubscriptionDetails()?.paymentStatus;
    return (s === PAYMENT_STATUS.UNPAID || s === PAYMENT_STATUS.UNSUBSCRIBED) && !this.orgIsTrial();
  });

  public readonly orgIsCancelPending = computed(() =>
    !!this._orgSubscriptionDetails()?.cancelPendingAtPeriodEnd
  );

  public readonly orgIsDowngradePending = computed(() =>
    !!this._orgSubscriptionDetails()?.pendingDowngrade
  );

  public readonly orgHasSubscriptionEnded = computed(() => {
    const sub = this._orgSubscriptionDetails();
    if (!sub) return true;
    if (sub.status === SUBSCRIPTION_STATUS.INACTIVE) return true;
    if (sub.status === SUBSCRIPTION_STATUS.ACTIVE) {
      if (this.orgIsTrial()) return false;
      if (sub.paymentStatus === PAYMENT_STATUS.PAID) return false;
      return true;
    }
    return false;
  });

  public readonly orgHasActiveStripeSubscription = computed(() =>
    !!(this._orgSubscriptionDetails()?.subscriptionId && this.orgHasPaid() && this.orgIsActive())
  );

  // Cache for the observable to avoid multiple HTTP requests
  private subscriptionCache$?: Observable<any>;
  private orgSubscriptionCache$?: Observable<any>;

  /**
   * Fetches subscription details from the API and updates the signal.
   * Subsequent calls use the cached observable to avoid redundant requests.
   * 
   * @returns Observable<any>
   */
  public fetchSubscriptionDetails(): Observable<any> {
    // Return cached observable if it exists
    if (this.subscriptionCache$) {
      return this.subscriptionCache$;
    }

    // Create new observable with caching
    this.subscriptionCache$ = this.organizationService.getSubscriptionDetails().pipe(
      tap((res) => {
        if (res.success && res.data) {
          let data = res.data as ISubscriptionResponse;
          this._subscriptionDetails.set(data);
        }
      }),
      shareReplay(1) // Share the result with all subscribers
    );

    return this.subscriptionCache$;
  }

  /**
   * Refreshes subscription details by clearing cache and fetching new data.
   * 
   * ⚠️ ONLY call this when subscription data has actually changed:
   * - After successful payment
   * - After plan upgrade/downgrade
   * - After subscription renewal
   * - When a different user signs in
   * 
   * @param userId - Optional user ID to track which user the subscription belongs to
   * @returns Observable<any>
   * 
   * @example
   * // After payment success:
   * onPaymentComplete() {
   *   this.subscriptionState.refreshSubscriptionDetails().subscribe();
   * }
   */
  public refreshSubscriptionDetails(userId?: string): Observable<any> {
    this.subscriptionCache$ = undefined;
    this._isDialogDismissed.set(false); // Reset dialog dismissal on refresh
    this._isSidebarLocked.set(false); // Reset sidebar lock on refresh

    if (userId && userId !== this._subscriptionUserId) {
      this._subscriptionDetails.set(null);
    }
    if (userId) {
      this._subscriptionUserId = userId;
    }
    return this.fetchSubscriptionDetails();
  }

  /**
   * Manually set subscription details (useful for testing or manual updates).
   * 
   * @param details - The subscription response to set
   */
  public setSubscriptionDetails(details: ISubscriptionResponse | null): void {
    this._subscriptionDetails.set(details);
  }

  /**
   * Imperatively show the subscription ended dialog from any component.
   * Resets any prior dismissal so the dialog is guaranteed to appear.
   */
  public triggerSubscriptionDialog(): void {
    this._isDialogDismissed.set(false);
    this.showSubscriptionDialog$.next();
  }

  /**
   * Dismiss the subscription ended dialog
   */
  public dismissSubscriptionDialog(): void {
    this._isDialogDismissed.set(true);
  }

  /**
   * Reset dialog dismissal (useful after subscription refresh)
   */
  public resetDialogDismissal(): void {
    this._isDialogDismissed.set(false);
  }

  /**
   * Lock sidebar items (when user clicks "Activate your plan")
   */
  public lockSidebar(): void {
    this._isSidebarLocked.set(true);
  }

  /**
   * Unlock sidebar items (after subscription refresh)
   */
  public unlockSidebar(): void {
    this._isSidebarLocked.set(false);
  }

  /**
   * Check if sidebar should be locked
   */
  public readonly isSidebarLocked = computed(() => {
    return this._isSidebarLocked();
  });

  public fetchOrgSubscriptionDetails(): Observable<any> {
    if (this.orgSubscriptionCache$) {
      return this.orgSubscriptionCache$;
    }

    this.orgSubscriptionCache$ = this.paymentService.getSubscriptionDetails().pipe(
      tap(res => {
        if (res.success && res.data) {
          this._orgSubscriptionDetails.set(res.data as ISubscriptionDetails & { plan?: any });
        }
      }),
      shareReplay(1)
    );

    return this.orgSubscriptionCache$;
  }

  public refreshOrgSubscriptionDetails(): void {
    this.orgSubscriptionCache$ = undefined;
    this.fetchOrgSubscriptionDetails().subscribe();
  }

  public setOrgSubscriptionDetails(data: (ISubscriptionDetails & { plan?: any }) | null): void {
    this._orgSubscriptionDetails.set(data);
  }

  public clearOrgSubscriptionDetails(): void {
    this._orgSubscriptionDetails.set(null);
    this.orgSubscriptionCache$ = undefined;
  }

  /**
   * Clear subscription details from state.
   */
  public clearSubscriptionDetails(): void {
    this._subscriptionDetails.set(null);
    this.subscriptionCache$ = undefined;
    this._subscriptionUserId = null;
    this._isDialogDismissed.set(false); // Reset dialog dismissal when clearing
    this._isSidebarLocked.set(false); // Reset sidebar lock when clearing
    this._orgSubscriptionDetails.set(null);
    this.orgSubscriptionCache$ = undefined;
  }

  /**
   * Check if subscription details belong to the given user ID.
   * Returns false if subscription details don't exist or user ID doesn't match.
   */
  public belongsToUser(userId: string): boolean {
    return this._subscriptionUserId === userId && this._subscriptionDetails() !== null;
  }
}
